import { ChoiceRow, Field } from '@/components/molecules';
import { dwrClassesById } from '@/data/classes';
import { ALIGNMENT_IDS, type AlignmentChoice, type AlignmentId } from '@/schemas/alignment';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';

const FALLBACK_PRECEPTS: Record<AlignmentId, string> = {
  buono: 'Aiuti chi è in difficoltà a costo personale.',
  legale: 'Rispetti la legge del tuo ordine.',
  neutrale: "Cerchi l'equilibrio fra opposti.",
  caotico: "Sfidi un'autorità ingiusta.",
  malvagio: 'Imponi la tua volontà con la forza.',
};

const ALIGNMENT_LABELS: Record<AlignmentId, string> = {
  buono: 'Buono',
  legale: 'Legale',
  neutrale: 'Neutrale',
  caotico: 'Caotico',
  malvagio: 'Malvagio',
};

/**
 * Step 5 — Allineamento.
 *
 * Re-design Fase 2 PR 5: 5 <ChoiceRow> (riuso PR 4) + textarea editabile per
 * il precept quando alignment selezionato. La desc nella ChoiceRow è la
 * preview del precept (dalla classe o fallback generico); la textarea
 * permette di customizzare il valore effettivo nel draft.
 *
 * **Memoria DWR**: ogni classe può avere ogni allineamento — niente vincoli
 * `allowedAlignments`. Il giocatore sceglie liberamente.
 */
export function SectionAlignment() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;
  const currentId = draft.alignment?.id;
  const currentPrecept = draft.alignment?.precept ?? '';

  function preceptFor(id: AlignmentId): string {
    return classDef?.alignmentPrecepts[id] ?? FALLBACK_PRECEPTS[id];
  }

  function handleSelect(id: AlignmentId) {
    const precept = id === currentId ? currentPrecept : preceptFor(id);
    dispatch({
      type: 'SET_ALIGNMENT',
      alignment: { id, precept: precept || `Precept ${id}` } satisfies AlignmentChoice,
    });
  }

  function handlePreceptChange(precept: string) {
    if (!currentId) return;
    dispatch({
      type: 'SET_ALIGNMENT',
      alignment: { id: currentId, precept } satisfies AlignmentChoice,
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-[0.4rem]">
        {ALIGNMENT_IDS.map((id) => (
          <ChoiceRow
            key={id}
            title={ALIGNMENT_LABELS[id]}
            description={preceptFor(id)}
            selected={currentId === id}
            onClick={() => handleSelect(id)}
          />
        ))}
      </div>

      {currentId ? (
        <Field
          label="Precetto · azione che fa segnare PE"
          htmlFor="precept-input"
          hint={
            classDef && classDef.alignmentPrecepts[currentId] !== currentPrecept ? (
              <>
                Suggerimento dalla classe: <em>{preceptFor(currentId)}</em>
              </>
            ) : undefined
          }
        >
          <textarea
            id="precept-input"
            value={currentPrecept}
            onChange={(e) => handlePreceptChange(e.target.value)}
            rows={3}
          />
        </Field>
      ) : null}
    </div>
  );
}
